import React, { useEffect, useState } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';

import ContentSectionHeader from '../../atoms/content-section-header/ContentSectionHeader';
import { ButtonScroll } from '../../atoms/icons/ScrollButton';
import TagList from '../../atoms/tag-list/TagList';

import { DEQ_COMPS } from '../../../utils/deq';
import { formatDate, ReactMarkdownExtended } from '../../../utils/generic';
import { renderBodyItem } from '../../../utils/generic/renderBodyItem';
import { BUTTON_LABELS } from '../../../utils/language';
import languageHelper from '../../../utils/language/language-helper';
import { RichTextHtml } from '../../../utils/richtext/richTextRender';
import { getTagsByPriority, getTagsByTypes } from '../../../utils/tags';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import Spacings from '../../../styles/Spacings';

import { TagTypes } from '../../../tokens';
import ContentWrapper from '../../molecules/content-wrapper/ContentWrapper';
import FilterableArticlePreviews from '../../molecules/filterable-article-previews/FilterableArticlePreviews';
import ProductOverviewCard from '../../molecules/product-overview-card/ProductOverviewCard';
import Promovisual from '../../molecules/promovisual/Promovisual';
import VideoCTA from '../../molecules/video-cta/VideoCTA';

const DeprecatedArticleLayout = ({ intakeCard, articlesData, data, tags }) => {
  const [scrollTop, setScrollTop] = useState(0);
  const { relatedProductTile, relatedProductCard, feedbackCard } = data;
  const { lang } = languageHelper();

  const fadeOut = () => {
    const target = document.getElementById('scrollButton');

    target.style.opacity = '0';
    target.addEventListener('transitionend', () => target.remove());
  };

  const handleScrollButton = () => {
    const element = document.getElementById('desc');

    element.scrollIntoView({ behavior: 'smooth' });

    fadeOut();
  };

  const onScroll = (e) => {
    setScrollTop(e.target.documentElement.scrollTop);

    const target = document.getElementById('scrollButton');

    if (target && scrollTop !== 0) {
      fadeOut();
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, [scrollTop]);

  const imageStyle = { width: '100%', height: '100%' };

  if (!data) return null;

  const filteredTags = getTagsByTypes(tags, [
    TagTypes.SECTOR,
    TagTypes.CATEGORY,
  ]);

  const filteredTagsByPriority = getTagsByPriority(tags || [], [
    TagTypes.CATEGORY,
    TagTypes.TOPIC,
    TagTypes.SECTOR,
  ]);

  const articles = articlesData?.filter((article) => {
    if (article?.title === data?.title) {
      return false;
    }

    return article?.tags?.some((tag) =>
      filteredTags.some((filteredTag) => tag.id === filteredTag.id)
    );
  });

  return (
    <>
      <ArticleHeader>
        <ImgContainer>
          <GatsbyImage
            alt=""
            image={data?.mainImage.gatsbyImageData}
            imgStyle={imageStyle}
            style={imageStyle}
          />
          <ButtonScrollContainer id="scrollButton" onClick={handleScrollButton}>
            <ButtonScroll />
          </ButtonScrollContainer>
        </ImgContainer>
        <TextContainer>
          <SubHeader>{data?.subHeader}</SubHeader>
          <Title>{data?.title}</Title>
          <TagList tags={filteredTagsByPriority} />
        </TextContainer>
        <SmallNotesRow id="desc">
          <SmallNote>
            {`${formatDate(new Date(data?.publishingTime))} ${
              data?.authorRef && data?.authorRef?.name
                ? `- ${data?.authorRef?.name}`
                : ''
            }`}
          </SmallNote>
          <SmallNote>
            {`${
              data.articleType === 'podcast'
                ? 'Luistertijd'
                : data.articleType === 'talkshow'
                ? 'Lengte'
                : 'Leestijd'
            }: ca. ${data?.readingMinutes} minuten`}
          </SmallNote>
        </SmallNotesRow>
      </ArticleHeader>
      <ContentWrapper>
        <FlexWrapper>
          <ArticleContent stickyCardActive={!!relatedProductTile}>
            <Lead>
              {data?.leadBody ? (
                <ReactMarkdownExtended>
                  {data.leadBody.childMarkdownRemark.rawMarkdownBody}
                </ReactMarkdownExtended>
              ) : (
                data?.lead?.lead
              )}
            </Lead>
            {data?.articleBody?.map((body) => (
              <DescriptionContainer key={body.id} hasHeader={body.header}>
                {body.header && (
                  <DescriptionHeader>{body.header}</DescriptionHeader>
                )}
                <Description>
                  {body.body ? (
                    <ReactMarkdownExtended>
                      {body.body.childMarkdownRemark.rawMarkdownBody}
                    </ReactMarkdownExtended>
                  ) : (
                    <RichTextHtml
                      richTextNode={body.paragraphText}
                      deqCompType={DEQ_COMPS.articleLayout}
                    />
                  )}
                </Description>
              </DescriptionContainer>
            ))}
          </ArticleContent>
          {relatedProductTile && (
            <StyledProductOverviewCard
              layout="portrait"
              title={relatedProductTile.title}
              subtitle={relatedProductTile.subtitle}
              image={relatedProductTile.image}
              body={relatedProductTile.body}
              primaryLink={
                relatedProductTile.ctaLinkPrimary ||
                relatedProductTile.primaryLink
              }
              service
            />
          )}
        </FlexWrapper>
        <DescriptionContainer hasHeader />
        <RelatedContainer>
          <div>
            {articles && articles.length > 0 && (
              <StyledArticlePreviews articlesArray={articles}>
                <ContentSectionHeader
                  title={BUTTON_LABELS.viewTips[lang]}
                  tags={filteredTags}
                />
              </StyledArticlePreviews>
            )}
          </div>
          {relatedProductCard !== null ? (
            <div>
              {relatedProductCard.body?.raw && (
                <Promovisual
                  reverse
                  thumbnail={relatedProductCard.image}
                  description={relatedProductCard.body}
                  title={relatedProductCard.title}
                  ctaText={relatedProductCard.ctaText}
                  primaryLink={relatedProductCard.ctaLinkPrimary}
                  secondaryLink={relatedProductCard.ctaLinkSecondary}
                />
              )}
              {relatedProductCard.body?.childMarkdownRemark && (
                <Promovisual
                  reverse
                  thumbnail={relatedProductCard.image}
                  title={relatedProductCard.title}
                  primaryLink={relatedProductCard?.primaryLink}
                  secondaryLink={relatedProductCard?.secondaryLink}
                >
                  <ReactMarkdownExtended
                    componentType={DEQ_COMPS.articleLayout}
                  >
                    {
                      relatedProductCard.body.childMarkdownRemark
                        .rawMarkdownBody
                    }
                  </ReactMarkdownExtended>
                </Promovisual>
              )}
            </div>
          ) : (
            <VideoCTA
              title={intakeCard?.title}
              body={intakeCard?.body}
              video={intakeCard?.animation}
              label={intakeCard?.ctaPrimaryLink.label}
              to={`/${intakeCard?.ctaPrimaryLink.linkedPage.slug}/`}
            />
          )}
          {feedbackCard &&
            renderBodyItem(
              feedbackCard,
              0,
              'magazine',
              [],
              data.slug,
              data.parentPage
            )}
        </RelatedContainer>
      </ContentWrapper>
    </>
  );
};

export default DeprecatedArticleLayout;

const StyledProductOverviewCard = styled(ProductOverviewCard)`
  margin: 48px 0 0;
  padding: 16px;

  ${mq.from.breakpoint.M`
    margin: 64px 0 0;
    position: sticky;
    top: 64px;
    width: calc(4/12*100%);
  `}

  ${mq.from.breakpoint.L`
    width: calc(33%); 
 `}

  ${mq.from.breakpoint.XL`
    flex-direction: column;
    margin: 120px 0 0;
    padding: 32px 24px;

    > div:first-of-type {
      margin: 0;
      align-self: center;
    }
  `}
`;

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${mq.from.breakpoint.M`
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    position: relative;
  `}
`;

const ArticleContent = styled.div`
  width: 100%;

  ${mq.from.breakpoint.M`
    width: calc(8/12*100%);
    margin: 0 auto;
  `}

  ${mq.from.breakpoint.L`
    width: calc(9/12*100%);
  `}

  ${mq.from.breakpoint.XL`
    width: calc(8/12*100%);
  `}

  ${({ stickyCardActive }) =>
    stickyCardActive &&
    css`
      ${mq.from.breakpoint.M`
        width: calc(7/12*100%);
      `}

      ${mq.from.breakpoint.L`
        width: calc(8/12*100%);
      `}

      ${mq.from.breakpoint.XL`
        width: calc(7/12*100%);
      `}
    `}
`;

const ArticleHeader = styled.div`
  display: flex;
  flex-direction: column;

  ${mq.from.breakpoint.L`
    height: 100vh;
    flex-direction: row;
    justify-content: space-between;
  `}
`;

const ImgContainer = styled.div`
  order: 2;
  width: 100%;

  ${mq.from.breakpoint.L`
    width: calc(50% - 40px);
    order: 0;
  `}

  @media screen and (min-width: 375px) and (max-width: 600px) {
    width: 100vw;
    height: 100vw;
  }
`;

const TextContainer = styled.div`
  align-self: center;
  margin: 32px 16px 24px;
  order: 0;
  width: calc(100% - 32px);

  ${mq.from.breakpoint.M`
    width: 90%;
    margin: 48px auto 40px;
  `}

  ${mq.from.breakpoint.L`
    padding-right: 5vw;
    box-sizing: border-box;
    margin: -160px 0 0;
    order: 1;
    width: calc(50% - 40px);
  `}
`;

const SubHeader = styled.div`
  align-self: flex-end;
  font-size: 16px;
  font-style: normal;
  font-weight: normal;
  line-height: 16px;
  margin-bottom: 32px;

  ${mq.from.px(1366)`
    font-size: 24px;
    line-height: 24px;
  `}
`;

const SmallNote = styled.p`
  font-size: 16px;
  font-style: normal;
  font-weight: normal;
  line-height: 16px;

  &:first-of-type {
    margin-right: 32px;
  }
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.fontCondensed};
  font-size: 48px;
  font-style: normal;
  font-weight: normal;
  letter-spacing: -0.03em;
  line-height: 48px;
  word-break: break-word;

  ${mq.from.breakpoint.L`
    font-size: 64px;
    line-height: 64px;
  `}

  ${mq.from.px(1920)`
    font-size: 72px;
    line-height: 72px;
  `}
`;

const Lead = styled.div`
  color: ${Colors.black};
  font-size: 16px;
  line-height: 24px;
  margin: 48px auto;
  width: 100%;

  ${mq.from.breakpoint.M`
    font-size: 24px;
    line-height: 32px;
    margin: 64px 0;
  `}

  ${mq.from.breakpoint.XL`
    margin: 120px 0 64px;
  `}
`;

const Description = styled.div`
  color: ${Colors.abnBlack};
  font-family: ${({ theme }) => theme.fontRegular};
  font-size: 16px;
  line-height: 24px;
`;

const DescriptionContainer = styled.div`
  align-self: center;
  margin-top: ${({ hasHeader }) => (hasHeader ? '48px' : '0')};
  width: 100%;

  ${mq.from.breakpoint.L`
    margin: ${({ hasHeader }) => (hasHeader ? '64px 0 0' : '0')};
  `}
`;

const DescriptionHeader = styled.h2`
  align-self: center;
  font-size: 24px;
  font-style: normal;
  font-weight: normal;
  line-height: 1;
  margin-bottom: 16px;

  ${mq.from.breakpoint.M`
    margin-bottom: 24px;
    font-size: 32px;
  `}
`;

const SmallNotesRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 16px;
  order: 1;
  width: calc(100% - 32px);

  ${mq.from.breakpoint.M`
    width: 90%;
    margin: 0 auto 40px;
  `}

  ${mq.from.breakpoint.L`
    width: calc(50% - 40px);
    justify-content: flex-start;
    position: absolute;
    right: 0;
    margin: 0;
    top: 90vh;
  `}
`;

const ButtonScrollContainer = styled.div`
  cursor: pointer;
  left: calc(50% - 70px);
  position: fixed;
  top: 90vh;
  transition: opacity 1.5s;
`;

const RelatedContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 48px;
  margin-top: 48px;

  > * {
    margin-top: 80px;

    ${mq.from.breakpoint.L`
      margin-top: 120px;
    `}

    &:first-child {
      margin-top: 0;
    }
  }

  ${mq.from.breakpoint.L`
    margin-top: ${Spacings.XXXL};
    margin-bottom: ${Spacings.XXXL};
  `}

  ${mq.from.breakpoint.XL`
    margin-top: 80px;
    margin-bottom: 80px;
  `}
`;

const StyledArticlePreviews = styled(FilterableArticlePreviews)`
  margin-bottom: 80px;

  ${mq.from.breakpoint.L`
    margin-bottom: 120px;
  `}
`;
